import { TimerElapsedInput, computeActiveElapsedMs } from '../timer/timer-elapsed.util';
import { ScheduleType } from '../activities/activity-type.model';
import { ScheduleGroup } from '../settings/me.model';
import { getEnabledScheduleGroups, resolveScheduleGroupLabel } from './schedule-group.util';

export type DayStatusGroupState = 'COMPLETE' | 'ON_TRACK';
export type DayStatusOverallState = 'ALL_COMPLETE' | 'ON_TRACK' | 'TIGHT' | 'OVERRUN';

export interface DayStatus {
  dayStartLabel: string;
  dayWindowEndLabel: string;
  projectedFinishLabel: string;
  windowSlackMinutes: number;
  groups: DayStatusGroup[];
  overallState: DayStatusOverallState;
}

export interface DayStatusGroup {
  ordinal: ScheduleType;
  label: string;
  plannedMinutes: number;
  budgetMinutes: number | null;
  remainingMinutes: number;
  projectedFinishLabel: string;
  targetEndLabel: string | null;
  slackMinutes: number | null;
  state: DayStatusGroupState;
}

export interface DayStatusAllocationInput {
  activityTypeId: string;
  slotCount: number;
  minutesPerSlot: number;
  activityDurationMinutes?: number;
  completedSlots: number;
  scheduleType?: ScheduleType;
}

export interface ActiveBlockInput extends TimerElapsedInput {
  activityTypeId: string;
}

const TIGHT_SLACK_THRESHOLD_MINUTES = 30;
const DEFAULT_SCHEDULE_TYPE: ScheduleType = 'PRIMARY';

/**
 * Shared Today small-viewport breakpoint (max-width).
 * Used for compact Day status (JS matchMedia) and Today layout CSS
 * (sticky date + list selector, 44px touch targets) — not only wrapping.
 */
export const DAY_STATUS_COMPACT_MAX_WIDTH_PX = 767;

export function isDayStatusCompactViewport(width: number): boolean {
  return width <= DAY_STATUS_COMPACT_MAX_WIDTH_PX;
}

export function computeDayStatus(
  dayWindowStartLocalTime: string,
  dayWindowEndLocalTime: string | null | undefined,
  allocations: DayStatusAllocationInput[],
  scheduleGroups: ScheduleGroup[],
  nowMs: number,
  activeBlocks: ActiveBlockInput[] = []
): DayStatus {
  const now = new Date(nowMs);
  const dayStart = resolveCurrentDayWindowStart(now, dayWindowStartLocalTime);
  const dayWindowEnd = resolveCurrentDayWindowEnd(dayStart, dayWindowEndLocalTime);
  const elapsedMinutesByActivityId = activeBlocks.reduce((totals, block) => {
    const elapsedMinutes = computeActiveElapsedMs(block, nowMs) / 60_000;
    const existingMinutes = totals.get(block.activityTypeId) ?? 0;
    totals.set(block.activityTypeId, existingMinutes + elapsedMinutes);
    return totals;
  }, new Map<string, number>());

  const remainingMinutesByScheduleType = allocations.reduce((totals, allocation) => {
    const slotCount = Number.isFinite(allocation.slotCount) ? Math.max(0, Math.floor(allocation.slotCount)) : 0;
    const completedSlots = Number.isFinite(allocation.completedSlots)
      ? Math.max(0, Math.floor(allocation.completedSlots))
      : 0;
    const remainingSlots = Math.max(0, slotCount - completedSlots);
    const activityDurationMinutes = Number.isFinite(allocation.activityDurationMinutes)
      ? Math.max(0, Number(allocation.activityDurationMinutes))
      : null;
    const minutesPerSlot = Number.isFinite(allocation.minutesPerSlot) ? Math.max(0, allocation.minutesPerSlot) : 0;
    const effectiveMinutesPerSlot = activityDurationMinutes ?? minutesPerSlot;
    const remainingMinutes = remainingSlots * effectiveMinutesPerSlot;
    const elapsedDeductionMinutes = Math.min(
      elapsedMinutesByActivityId.get(allocation.activityTypeId) ?? 0,
      remainingMinutes
    );
    const effectiveRemainingMinutes = Math.max(0, remainingMinutes - elapsedDeductionMinutes);
    const scheduleType = allocation.scheduleType ?? DEFAULT_SCHEDULE_TYPE;
    totals.set(scheduleType, (totals.get(scheduleType) ?? 0) + effectiveRemainingMinutes);
    return totals;
  }, new Map<ScheduleType, number>());
  const plannedMinutesByScheduleType = allocations.reduce((totals, allocation) => {
    const slotCount = Number.isFinite(allocation.slotCount) ? Math.max(0, Math.floor(allocation.slotCount)) : 0;
    const activityDurationMinutes = Number.isFinite(allocation.activityDurationMinutes)
      ? Math.max(0, Number(allocation.activityDurationMinutes))
      : null;
    const minutesPerSlot = Number.isFinite(allocation.minutesPerSlot) ? Math.max(0, allocation.minutesPerSlot) : 0;
    const effectiveMinutesPerSlot = activityDurationMinutes ?? minutesPerSlot;
    const plannedMinutes = slotCount * effectiveMinutesPerSlot;
    const scheduleType = allocation.scheduleType ?? DEFAULT_SCHEDULE_TYPE;
    totals.set(scheduleType, (totals.get(scheduleType) ?? 0) + plannedMinutes);
    return totals;
  }, new Map<ScheduleType, number>());

  const enabledGroups = getEnabledScheduleGroups(scheduleGroups);
  const groups: DayStatusGroup[] = [];
  let cursorMs = nowMs;

  for (const group of enabledGroups) {
    const roundedRemainingMinutes = Math.round(remainingMinutesByScheduleType.get(group.ordinal) ?? 0);
    const roundedPlannedMinutes = Math.round(plannedMinutesByScheduleType.get(group.ordinal) ?? 0);
    const normalizedBudgetMinutes = Number.isFinite(group.budgetMinutes)
      ? Math.max(0, Math.round(Number(group.budgetMinutes)))
      : null;
    const projectedFinishMs = Math.max(cursorMs, nowMs) + roundedRemainingMinutes * 60_000;
    cursorMs = projectedFinishMs;

    const state = resolveGroupState(roundedRemainingMinutes);
    groups.push({
      ordinal: group.ordinal,
      label: resolveScheduleGroupLabel(group),
      plannedMinutes: roundedPlannedMinutes,
      budgetMinutes: normalizedBudgetMinutes,
      remainingMinutes: roundedRemainingMinutes,
      projectedFinishLabel: formatTime(new Date(projectedFinishMs)),
      targetEndLabel: null,
      slackMinutes: normalizedBudgetMinutes == null ? null : normalizedBudgetMinutes - roundedPlannedMinutes,
      state
    });
  }

  const projectedFinishMs = Math.max(cursorMs, nowMs);
  const windowSlackMinutes = Math.round((dayWindowEnd.getTime() - projectedFinishMs) / 60_000);
  const overallState = resolveOverallState(groups, windowSlackMinutes);

  return {
    dayStartLabel: formatTime(dayStart),
    dayWindowEndLabel: formatTime(dayWindowEnd),
    projectedFinishLabel: formatTime(new Date(projectedFinishMs)),
    windowSlackMinutes,
    groups,
    overallState
  };
}

function resolveCurrentDayWindowStart(now: Date, dayWindowStartLocalTime: string): Date {
  const { hours, minutes } = parseLocalTime(dayWindowStartLocalTime);
  const dayStart = new Date(now);
  dayStart.setHours(hours, minutes, 0, 0);
  if (now.getTime() < dayStart.getTime()) {
    dayStart.setDate(dayStart.getDate() - 1);
  }
  return dayStart;
}

function resolveCurrentDayWindowEnd(dayStart: Date, dayWindowEndLocalTime: string | null | undefined): Date {
  if (!dayWindowEndLocalTime) {
    return new Date(dayStart.getTime() + 12 * 60 * 60_000);
  }
  const { hours, minutes } = parseLocalTime(dayWindowEndLocalTime);
  const dayEnd = new Date(dayStart);
  dayEnd.setHours(hours, minutes, 0, 0);
  if (dayEnd.getTime() <= dayStart.getTime()) {
    dayEnd.setDate(dayEnd.getDate() + 1);
  }
  return dayEnd;
}

function parseLocalTime(value: string): { hours: number; minutes: number } {
  const parts = value.split(':');
  const parsedHours = Number(parts[0]);
  const parsedMinutes = Number(parts[1]);
  const hours = Number.isFinite(parsedHours) && parsedHours >= 0 && parsedHours <= 23 ? parsedHours : 0;
  const minutes = Number.isFinite(parsedMinutes) && parsedMinutes >= 0 && parsedMinutes <= 59 ? parsedMinutes : 0;
  return { hours, minutes };
}

function formatTime(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
}

export function resolveNextRemainingScheduleType(groups: DayStatusGroup[]): ScheduleType | null {
  if (groups.length === 0) {
    return null;
  }
  return groups.find((group) => group.state !== 'COMPLETE')?.ordinal ?? groups[0].ordinal;
}

function resolveGroupState(remainingMinutes: number): DayStatusGroupState {
  if (remainingMinutes <= 0) {
    return 'COMPLETE';
  }
  return 'ON_TRACK';
}

function resolveOverallState(groups: DayStatusGroup[], windowSlackMinutes: number): DayStatusOverallState {
  if (groups.length === 0 || groups.every((group) => group.state === 'COMPLETE')) {
    return 'ALL_COMPLETE';
  }
  if (windowSlackMinutes < 0) {
    return 'OVERRUN';
  }
  if (windowSlackMinutes <= TIGHT_SLACK_THRESHOLD_MINUTES) {
    return 'TIGHT';
  }
  return 'ON_TRACK';
}
